import React, { useState, lazy, Suspense, useEffect } from "react";
import { CpButton, CpInputField } from "canopy-styleguide!sofe";
import { useForm } from "react-hook-form";
import { useCss, k, a, m } from "kremling";
import { verifySetupMfaCode } from "./code-mfa.resource";
import { handleError } from "src/error";
import { CpLoader } from "@canopytax/understory";
import { fetcher } from "fetcher!sofe";
import Auth from "cp-client-auth!sofe";
import { successToast, warningToast } from "toast-service!sofe";

const QRCodeImage = lazy(() =>
  SystemJS.import("global-settings!sofe").then((globalSettings: any) =>
    globalSettings.getQRCodeImage().then((qrModule: any) => ({
      default: qrModule.QRCodeImage,
    }))
  )
);

type SetupMfaAuthSubmitProps = {
  mfaAuthResource: {
    id: string;
    secret: string;
  };
  limitedAccessSession: string;
  invalidateSession: () => void;
  onBack: () => void;
  mobile: boolean;
  postLoginRedirect: (showMultitenantSelect: boolean, user: any) => void;
};

type User = {
  email: string;
};

export function SetupMfaAuthSubmit({
  mfaAuthResource,
  limitedAccessSession,
  invalidateSession,
  onBack,
  mobile,
  postLoginRedirect,
}: SetupMfaAuthSubmitProps) {
  const scope = useCss(css);

  const [invalidCode, setInvalidCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<User>();
  const { handleSubmit, control, watch } = useForm({
    defaultValues: { code: "" },
    mode: "onSubmit",
  });
  const code = watch("code");

  useEffect(() => {
    // Using fetcher rather than Auth.refetchLoggedInUser() because we need to pass the limitedAccessSession header
    if (limitedAccessSession) {
      fetcher("/users/0", {
        passThrough401: true,
        method: "GET",
        credentials: "omit",
        headers: { "Limited-Access": limitedAccessSession },
      })
        .then(({ users }: any) => {
          setUser(users);
        })
        .catch(handleError);
    }
  }, [limitedAccessSession]);

  function submit(form: { code: string }) {
    setInvalidCode("");
    setLoading(true);

    verifySetupMfaCode(mfaAuthResource.id, { code: form.code.trim() }, limitedAccessSession)
      .then((response: any) => {
        const showMultitenantSelect =
          response?.show_multi_tenant_select && !(window?.appIsMobile && response?.instance_url.includes("/authorize"));
        loginComplete(showMultitenantSelect);
      })
      .catch((err: any) => {
        if (err.status === 401) {
          invalidateSession();
        } else if (err.status === 403) {
          setInvalidCode("Invalid code");
        } else {
          handleError(err);
        }
      })
      .finally(() => setLoading(false));
  }

  const loginComplete = (showMultitenantSelect: boolean) => {
    Promise.all([Auth.refetchLoggedInUser(), Auth.refetchTenant()]).then(([user]) =>
      postLoginRedirect(showMultitenantSelect, user)
    );
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(mfaAuthResource.secret);
      successToast("Code copied to clipboard");
    } catch (err) {
      handleError(err);
      warningToast("Failed to copy code");
    }
  };

  return (
    <div {...scope} className="setup-mfa-auth-submit-container">
      <div className="cp-mb-24 auth-message">
        <div className="cp-text-center">
          <strong>Scan QR Code</strong>
        </div>
        <div className="cp-text-center">
          <Suspense fallback={<CpLoader />}>
            {user ? <QRCodeImage userEmail={user.email} secret={mfaAuthResource.secret} /> : <CpLoader />}
          </Suspense>
        </div>

        <div className="cp-mb-16 cp-text-center">
          Or use this key in your authenticator app to generate a code:{" "}
          {mobile ? (
            <div>
              <CpButton
                btnType="flat"
                icon="crud-duplicate"
                aria-label="Click to copy"
                onClick={handleCopyCode}
                className="cp-ml-8"
              >
                {mfaAuthResource.secret}
              </CpButton>
            </div>
          ) : (
            <strong>{mfaAuthResource.secret}</strong>
          )}
        </div>
      </div>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(submit)();
        }}
      >
        <CpInputField
          error={invalidCode}
          placeholder="Enter authentication code"
          fieldName="code"
          control={control}
          type="code"
          rules={{
            minLength: {
              value: 1,
              message: "Invalid code",
            },
          }}
        />
        <div className={a("cp-mt-40").m("mobile-button-group", mobile)}>
          <CpButton type="submit" disabled={!code || loading}>
            Next
          </CpButton>
          <CpButton btnType="secondary" onClick={onBack} className={m("cp-ml-16", !mobile)} type="button">
            Back
          </CpButton>
        </div>
      </form>
    </div>
  );
}

const css = k`
  .mobile-button-group {
    display: flex;
    flex-direction: column;
    height: 12.8rem;
    justify-content: space-between;
  }
  .mobile-button-group .cp-button {
    width: 100%;
  }
`;
